import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  GameVariant,
  Color,
  PieceType,
  createInitialBoard,
  getAllLegalMoves,
  applyMove,
  getPiecesByColor,
  type BoardState,
  type Move,
} from '@draughts/core';
import type { CSSProperties } from 'react';
import { squareToSVGCoords, isPlayableSquare } from '../../utils/board-coordinates';
import Piece from '../game/Piece';
import { variantBoardColors } from '../game/board-themes';
import { PieceLodContext, useBoardLod } from '../game/piece-lod';
import { variantPieceColors } from '../game/piece-themes';
import './AnimatedShowcaseBoard.css';

const SQUARE_SIZE = 60;

interface AnimatedShowcaseBoardProps {
  className?: string;
}

// Predefined sequence for Italian 8x8 - coup with damatura
// 23-19, 11-14, 28-23, 6-11, 32-28, 11-15, 21-17, 15-20, 24x15, 14-18, 22x6, 2x20, 23x16, 9-13, 17x10, 5x32
const ITALIAN_MOVES: { from: number; to: number }[] = [
  { from: 23, to: 19 },
  { from: 11, to: 14 },
  { from: 28, to: 23 },
  { from: 6, to: 11 },
  { from: 32, to: 28 },
  { from: 11, to: 15 },
  { from: 21, to: 17 },
  { from: 15, to: 20 },
  { from: 24, to: 15 },  // capture
  { from: 14, to: 18 },
  { from: 22, to: 6 },   // capture
  { from: 2, to: 20 },   // capture
  { from: 23, to: 16 },  // capture
  { from: 9, to: 13 },
  { from: 17, to: 10 },  // capture
  { from: 5, to: 32 },   // capture + DAMATURA!
];

// Predefined sequence for International 10x10
const INTERNATIONAL_MOVES: { from: number; to: number }[] = [
  { from: 32, to: 28 },
  { from: 18, to: 23 },
  { from: 37, to: 32 },
  { from: 23, to: 29 },
  { from: 34, to: 23 },  // capture
  { from: 17, to: 22 },
  { from: 28, to: 17 },  // capture
  { from: 19, to: 26 },  // capture
  { from: 41, to: 37 },  // mossa qualsiasi bianco
  { from: 11, to: 22 },  // capture
];

// Predefined sequence for Brazilian 8x8 (flying kings, mandatory longest capture)
// 1. c3-b4   f6-g5      2. b2-c3   g5-h4     3. b4-a5   g7-f6
// 4. c3-d4   b6-c5      5. d4xb6   a7xc5     6. g3-f4   h8-g7
// 7. a1-b2   f6-g5      8. b2-c3   c5-b4     9. a3xc5   d6xb4
// 10. c3-d4  b4-a3     11. d4-e5  c7-b6    12. a5xc7  d8xb6!
// 13. d2-c3  b6-a5     14. c3-d4  a5-b4    15. e1-d2  b8-c7
// 16. d4-c5  b4xd6     17. e3-d4  g5xe3xc5
// 18. c1-b2  a3xc1xe3  19. f2xd4xb6xd8xf6xh8  (5-piece grand capture!)
const BRAZILIAN_MOVES: { from: number; to: number }[] = [
  { from: 22, to: 17 }, // 1. c3-b4
  { from: 11, to: 16 }, //    f6-g5
  { from: 25, to: 22 }, // 2. b2-c3
  { from: 16, to: 20 }, //    g5-h4
  { from: 17, to: 13 }, // 3. b4-a5
  { from:  8, to: 11 }, //    g7-f6
  { from: 22, to: 18 }, // 4. c3-d4
  { from:  9, to: 14 }, //    b6-c5
  { from: 18, to:  9 }, // 5. d4xb6     (capture c5)
  { from:  5, to: 14 }, //    a7xc5     (capture b6)
  { from: 24, to: 19 }, // 6. g3-f4
  { from:  4, to:  8 }, //    h8-g7
  { from: 29, to: 25 }, // 7. a1-b2
  { from: 11, to: 16 }, //    f6-g5
  { from: 25, to: 22 }, // 8. b2-c3
  { from: 14, to: 17 }, //    c5-b4
  { from: 21, to: 14 }, // 9. a3xc5     (capture b4)
  { from: 10, to: 17 }, //    d6xb4     (capture c5)
  { from: 22, to: 18 }, // 10. c3-d4
  { from: 17, to: 21 }, //     b4-a3
  { from: 18, to: 15 }, // 11. d4-e5
  { from:  6, to:  9 }, //     c7-b6
  { from: 13, to:  6 }, // 12. a5xc7    (capture b6)
  { from:  2, to:  9 }, //     d8xb6    (capture c7)
  { from: 26, to: 22 }, // 13. d2-c3
  { from:  9, to: 13 }, //     b6-a5
  { from: 22, to: 18 }, // 14. c3-d4
  { from: 13, to: 17 }, //     a5-b4
  { from: 31, to: 26 }, // 15. e1-d2
  { from:  1, to:  6 }, //     b8-c7
  { from: 18, to: 14 }, // 16. d4-c5
  { from: 17, to: 10 }, //     b4xd6    (capture c5)
  { from: 23, to: 18 }, // 17. e3-d4
  { from: 16, to: 14 }, //     g5xe3xc5 (double: capture f4, d4)
  { from: 30, to: 25 }, // 18. c1-b2
  { from: 21, to: 23 }, //     a3xc1xe3 (double: capture b2, d2)
  { from: 27, to:  4 }, // 19. f2xd4xb6xd8xf6xh8 (5-piece: e3,c5,c7,e7,g7)
];

// Predefined sequence for English 8x8 (WCDF rules, black starts)
// 9-13, 23-19, 6-9, 27-23, 9-14, 22-18, 11-15, 18x11, 8x15, 23-18, 14x23, 19-16,
// 12x19, 21-17, 13x22, 25x11(double), 7x16, 24x6(double), 1x10, 26x12(double)
const ENGLISH_MOVES: { from: number; to: number }[] = [
  { from: 9, to: 13 },
  { from: 23, to: 19 },
  { from: 6, to: 9 },
  { from: 27, to: 23 },
  { from: 9, to: 14 },
  { from: 22, to: 18 },
  { from: 11, to: 15 },
  { from: 18, to: 11 },  // capture
  { from: 8, to: 15 },   // capture
  { from: 23, to: 18 },
  { from: 14, to: 23 },  // capture
  { from: 19, to: 16 },
  { from: 12, to: 19 },  // capture
  { from: 21, to: 17 },
  { from: 13, to: 22 },  // capture
  { from: 25, to: 11 },  // double capture!
  { from: 7, to: 16 },   // capture
  { from: 24, to: 6 },   // double capture!
  { from: 1, to: 10 },   // capture
  { from: 26, to: 12 },  // double capture!
];

// Get moves for a variant
const getMovesForVariant = (variant: GameVariant) => {
  if (variant === GameVariant.INTERNATIONAL) {
    return INTERNATIONAL_MOVES;
  }
  if (variant === GameVariant.ENGLISH) {
    return ENGLISH_MOVES;
  }
  if (variant === GameVariant.BRAZILIAN) {
    return BRAZILIAN_MOVES;
  }
  return ITALIAN_MOVES;
};

// Randomly select a variant
const getRandomVariant = (): GameVariant => {
  const variants = [GameVariant.ITALIAN, GameVariant.INTERNATIONAL, GameVariant.ENGLISH, GameVariant.BRAZILIAN];
  return variants[Math.floor(Math.random() * variants.length)] ?? GameVariant.ITALIAN;
};

// Animation phases
type AnimationPhase = 'waiting' | 'moving' | 'pausing' | 'finished';

/**
 * Le quattro combinazioni di colore e tipo, costruite una volta sola.
 *
 * `Piece` è `memo()`, ma riceveva `piece={{ color, type, square: 0 }}` costruito
 * dentro il render: un oggetto NUOVO a ogni frame, quindi il confronto shallow
 * falliva sempre e la memoizzazione non ha mai protetto niente. Con
 * l'animazione che fa `setAnimationProgress` a ogni frame, il risultato era che
 * TUTTI i pezzi — fino a 40 su una 10x10 — venivano ridisegnati per intero 60
 * volte al secondo, ognuno con i suoi gradienti, la sua maschera e un filtro a
 * due `feDropShadow`. Misurato sulla landing di produzione: la sola scacchiera
 * valeva circa un quarto di core, da fermi.
 *
 * `square` non lo legge nessuno — la posizione a `Piece` arriva da `x`/`y` — per
 * cui una costante condivisa per combinazione basta, e l'identità resta stabile
 * fra un frame e l'altro.
 */
const PROTOTIPI_PEZZO = new Map<string, { color: Color; type: PieceType; square: number }>();
function prototipoPezzo(color: Color, type: PieceType) {
  const chiave = `${color}-${type}`;
  let p = PROTOTIPI_PEZZO.get(chiave);
  if (!p) {
    p = { color, type, square: 0 };
    PROTOTIPI_PEZZO.set(chiave, p);
  }
  return p;
}

export default function AnimatedShowcaseBoard({ className = '' }: AnimatedShowcaseBoardProps) {
  const { t } = useTranslation();

  // Randomly select variant on mount
  const [variant] = useState(() => getRandomVariant());
  const boardSize = variant === GameVariant.INTERNATIONAL ? 10 : 8;
  const moves = getMovesForVariant(variant);
  const variantLabel = variant === GameVariant.INTERNATIONAL
    ? t('game.variants.internationalShort')
    : variant === GameVariant.ENGLISH
      ? t('game.variants.englishShort')
      : variant === GameVariant.BRAZILIAN
        ? t('game.variants.brazilianShort')
        : t('game.variants.italianShort');
  const boardPixelSize = boardSize * SQUARE_SIZE;
  // English uses international-style orientation (darkSquareBottomRight = false)
  const darkSquareBottomRight = variant === GameVariant.ITALIAN;
  // Illustrative board: always use the variant's MasterMatrix colors.
  // In `useMemo` e non chiamate secche: `variantPieceColors` ricostruisce la coppia
  // ogni volta (due gradienti derivati con quattro schiarite/scurite ciascuno), e il
  // suo risultato è la prop `colorPair` di ogni `Piece`. Nuova identità a ogni frame
  // = memo dei pezzi annullata, esattamente come per l'oggetto `piece`. `variant` è
  // fissata al montaggio, quindi qui si calcolano una volta e basta.
  const boardColors = useMemo(() => variantBoardColors(variant), [variant]);
  const pieceColorPair = useMemo(() => variantPieceColors(variant), [variant]);
  const boardSvgRef = useRef<SVGSVGElement>(null);
  const pieceLod = useBoardLod(boardSvgRef, boardPixelSize, SQUARE_SIZE * 0.8);

  // Game state
  const [board, setBoard] = useState<BoardState>(() => createInitialBoard(variant));
  const [phase, setPhase] = useState<AnimationPhase>('waiting');
  const [currentMove, setCurrentMove] = useState<Move | null>(null);
  const [animationProgress, setAnimationProgress] = useState(0);
  const [moveIndex, setMoveIndex] = useState(0);

  const animationRef = useRef<number | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Get coordinates for a square
  const getCoords = useCallback((square: number) => {
    return squareToSVGCoords(square, SQUARE_SIZE, boardSize, darkSquareBottomRight);
  }, [boardSize, darkSquareBottomRight]);

  // Reset game
  const resetGame = useCallback(() => {
    setBoard(createInitialBoard(variant));
    setPhase('waiting');
    setCurrentMove(null);
    setAnimationProgress(0);
    setMoveIndex(0);
  }, [variant]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  /*
   * Quando la partita ha il permesso di andare avanti.
   *
   * Tre condizioni: chi chiede meno movimento non la fa partire affatto (il
   * @media in AnimatedShowcaseBoard.css spegne solo le transizioni CSS, il loop
   * in JS lo ignorava); a scacchiera scrollata fuori dallo schermo si ferma; a
   * scheda in secondo piano pure. L'ultima non è ridondante: `rAF` il browser lo
   * sospende da sé, ma i `setTimeout` delle fasi no, e quelli fanno avanzare la
   * partita — con una `getAllLegalMoves` per mossa — anche a pagina non vista.
   *
   * Chi si ferma lo fa sempre su una posizione INTERA: la mossa eventualmente a
   * metà viene chiusa subito (vedi il loop qui sotto). Senza, il pezzo resterebbe
   * fra due caselle e il pezzo mangiato a mezza opacità — e con la preferenza di
   * movimento ridotto, che non si spegne da sola, ci resterebbe per sempre.
   */
  const [attivo, setAttivo] = useState(true);
  useEffect(() => {
    const menoMovimento = window.matchMedia('(prefers-reduced-motion: reduce)');
    let inVista = true;
    const calcola = () => setAttivo(!menoMovimento.matches && inVista && !document.hidden);

    const el = boardSvgRef.current;
    const osservatore = el && typeof IntersectionObserver !== 'undefined'
      // L'ULTIMA voce, non la prima: il browser può consegnare più osservazioni
      // in un colpo solo, e le precedenti sono già superate — leggendo `[0]` la
      // scacchiera potrebbe restare ferma credendosi ancora fuori schermo.
      ? new IntersectionObserver((voci) => {
          const ultima = voci[voci.length - 1];
          inVista = ultima?.isIntersecting ?? true;
          calcola();
        })
      : null;
    if (el && osservatore) osservatore.observe(el);

    document.addEventListener('visibilitychange', calcola);
    menoMovimento.addEventListener('change', calcola);
    calcola();

    return () => {
      osservatore?.disconnect();
      document.removeEventListener('visibilitychange', calcola);
      menoMovimento.removeEventListener('change', calcola);
    };
  }, []);

  // Find the matching legal move
  const findLegalMove = useCallback((boardState: BoardState, from: number, to: number): Move | null => {
    const legalMoves = getAllLegalMoves(boardState);
    return legalMoves.find(m => m.from === from && m.to === to) ?? null;
  }, []);

  // Main game loop
  useEffect(() => {
    // Niente da programmare finche' la partita e' in pausa: senza questo, la
    // catena timeout -> fase -> timeout continuerebbe a girare a pagina nascosta.
    if (!attivo) {
      // Ci si ferma solo su una posizione INTERA. Una mossa interrotta a meta'
      // lascia il pezzo fra due caselle e quello mangiato a mezza opacita':
      // tornando visibile si sistemerebbe da se', ma il movimento ridotto non
      // "torna" — e quel fotogramma storto resterebbe li' per sempre. Chiuderla
      // costa una transizione, e la posizione e' comunque quella a cui la
      // partita sarebbe arrivata un istante dopo.
      if (phase === 'moving' && currentMove) {
        setBoard(prev => applyMove(prev, currentMove));
        setMoveIndex(i => i + 1);
        setCurrentMove(null);
        setAnimationProgress(0);
        setPhase('pausing');
      }
      return;
    }

    if (phase === 'waiting') {
      const delay = moveIndex === 0 ? 2000 : 600;

      timeoutRef.current = setTimeout(() => {
        if (moveIndex >= moves.length) {
          setPhase('finished');
          return;
        }

        const moveData = moves[moveIndex];
        if (!moveData) {
          setPhase('finished');
          return;
        }

        const move = findLegalMove(board, moveData.from, moveData.to);
        if (move) {
          setCurrentMove(move);
          setPhase('moving');
          setAnimationProgress(0);
        } else {
          setMoveIndex(i => i + 1);
        }
      }, delay);

    } else if (phase === 'moving' && currentMove) {
      const startTime = Date.now();
      // Duration based on number of captures (more captures = more time for each hop)
      const numCaptures = currentMove.captures.length;
      const duration = numCaptures > 1 ? 140 * numCaptures : (numCaptures === 1 ? 180 : 350);

      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        // Use linear progress for multi-captures (easing applied per segment)
        // Use eased progress for simple moves
        const effectiveProgress = numCaptures > 1 ? progress : (1 - Math.pow(1 - progress, 3));
        setAnimationProgress(effectiveProgress);

        if (progress < 1) {
          animationRef.current = requestAnimationFrame(animate);
        } else {
          setBoard(prev => applyMove(prev, currentMove));
          setMoveIndex(i => i + 1);
          setCurrentMove(null);
          setPhase('pausing');
        }
      };

      animationRef.current = requestAnimationFrame(animate);
    } else if (phase === 'pausing') {
      timeoutRef.current = setTimeout(() => {
        setPhase('waiting');
      }, 300);
    } else if (phase === 'finished') {
      timeoutRef.current = setTimeout(() => {
        resetGame();
      }, 3000);
    }

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [attivo, phase, board, currentMove, moveIndex, moves, resetGame, findLegalMove]);

  // Build the full path for animation (from + path intermediates)
  const getFullPath = useCallback((move: Move): number[] => {
    if (move.path && move.path.length > 0) {
      return [move.from, ...move.path];
    }
    return [move.from, move.to];
  }, []);

  // Calculate which segment we're in and the progress within that segment
  const getSegmentInfo = useCallback((move: Move, progress: number) => {
    const fullPath = getFullPath(move);
    const numSegments = fullPath.length - 1;

    if (numSegments <= 1) {
      return { segmentIndex: 0, segmentProgress: progress, fromSquare: fullPath[0], toSquare: fullPath[1] };
    }

    const segmentLength = 1 / numSegments;
    const segmentIndex = Math.min(Math.floor(progress / segmentLength), numSegments - 1);
    const segmentProgress = (progress - segmentIndex * segmentLength) / segmentLength;

    return {
      segmentIndex,
      segmentProgress: Math.min(segmentProgress, 1),
      fromSquare: fullPath[segmentIndex],
      toSquare: fullPath[segmentIndex + 1],
    };
  }, [getFullPath]);

  // Calculate animated piece position
  const getAnimatedPosition = useCallback(() => {
    if (!currentMove || animationProgress === 0) return null;

    const { segmentProgress, fromSquare, toSquare } = getSegmentInfo(currentMove, animationProgress);
    if (fromSquare === undefined || toSquare === undefined) return null;

    const from = getCoords(fromSquare);
    const to = getCoords(toSquare);

    // Ease the segment progress
    const eased = 1 - Math.pow(1 - segmentProgress, 3);

    const x = from.x + (to.x - from.x) * eased;
    const baseY = from.y + (to.y - from.y) * eased;

    // Arc effect for captures
    if (currentMove.captures.length > 0) {
      const arcHeight = SQUARE_SIZE * 0.5;
      const arc = Math.sin(eased * Math.PI) * arcHeight;
      return { x, y: baseY - arc };
    }

    return { x, y: baseY };
  }, [currentMove, animationProgress, getCoords, getSegmentInfo]);

  // Calculate opacity for a captured piece based on when it gets captured
  const getCaptureOpacity = useCallback((captureIndex: number): number => {
    if (!currentMove || currentMove.captures.length === 0) return 1;

    const numCaptures = currentMove.captures.length;
    if (numCaptures === 1) {
      return 1 - animationProgress;
    }

    // For multiple captures, fade each piece when the moving piece passes over it
    const captureTime = (captureIndex + 0.5) / numCaptures;
    if (animationProgress < captureTime - 0.1) return 1;
    if (animationProgress > captureTime + 0.1) return 0;
    return 1 - (animationProgress - (captureTime - 0.1)) / 0.2;
  }, [currentMove, animationProgress]);

  // Get the piece being moved
  const movingPiece = currentMove ? board.pieces.find(p => p.square === currentMove.from) : null;
  const animatedPosition = getAnimatedPosition();

  // Render a piece — delegates to the shared <Piece> so it picks up the
  // user's piece shape/style/color theme from preferences-store.
  const renderPiece = (
    color: Color,
    type: PieceType,
    x: number,
    y: number,
    key: string,
    _isBeingCaptured = false,
    opacity = 1
  ) => {
    const size = SQUARE_SIZE * 0.8;
    return (
      <g key={key} style={{ opacity }}>
        <Piece piece={prototipoPezzo(color, type)} x={x} y={y} size={size} colorPair={pieceColorPair} />
      </g>
    );
  };

  // Get all pieces from board state
  const whitePieces = getPiecesByColor(board, Color.WHITE);
  const blackPieces = getPiecesByColor(board, Color.BLACK);

  return (
    <div className={`animated-showcase-board ${className}`}>
      <PieceLodContext.Provider value={pieceLod}>
      <svg
        ref={boardSvgRef}
        className="showcase-board-svg"
        viewBox={`0 0 ${boardPixelSize} ${boardPixelSize}`}
        style={{
          '--color-square-light': boardColors.light,
          '--color-square-dark': boardColors.dark,
        } as CSSProperties}
      >
        {/* Board squares */}
        {Array.from({ length: boardSize }, (_, row) =>
          Array.from({ length: boardSize }, (_, col) => {
            const isDark = isPlayableSquare(row + 1, col + 1, boardSize, darkSquareBottomRight);
            return (
              <rect
                key={`${row}-${col}`}
                x={col * SQUARE_SIZE}
                y={row * SQUARE_SIZE}
                width={SQUARE_SIZE}
                height={SQUARE_SIZE}
                className={isDark ? 'showcase-square-dark' : 'showcase-square-light'}
              />
            );
          })
        )}


        {/* Static pieces */}
        {[...whitePieces, ...blackPieces].map((piece) => {
          // Skip the piece being moved
          if (currentMove && piece.square === currentMove.from && animationProgress > 0) {
            return null;
          }
          // Check if being captured and get its index
          const captureIndex = currentMove?.captures.findIndex(c => c.square === piece.square) ?? -1;
          const isBeingCaptured = captureIndex >= 0;
          const captureOpacity = isBeingCaptured ? getCaptureOpacity(captureIndex) : 1;

          const coords = getCoords(piece.square);
          return renderPiece(
            piece.color,
            piece.type,
            coords.x,
            coords.y,
            `piece-${piece.square}`,
            isBeingCaptured,
            captureOpacity
          );
        })}

        {/* Moving piece */}
        {movingPiece && animatedPosition && (
          renderPiece(
            movingPiece.color,
            movingPiece.type,  // Keep original type during animation, promotion happens after applyMove
            animatedPosition.x,
            animatedPosition.y,
            'moving'
          )
        )}
      </svg>
      </PieceLodContext.Provider>

      {/* Variant label */}
      <div className="showcase-variant-label">
        {variantLabel}
      </div>
    </div>
  );
}
